/**
 * /account/undo-email-change#token=… — the link in the "your sign-in email changed" notice sent to the old address
 * (7 days). Reads the token from the fragment (never sent to the server in the URL), calls
 * POST /v1/account/email-change/undo, and shows the outcome: the old address is back and every session was signed
 * out, or the link expired / was already used. No session needed. Like Google's "Secure your account" link.
 */
import { errorKind } from "@bundle/core";
import { Button, Icon, Logo, Spinner, Stack, Surface, Text, useT } from "@bundle/ui";
import { useEffect, useState } from "react";
import { undoEmailChange } from "../platform/accountSecurity.ts";

export const UNDO_EMAIL_CHANGE_PATH = "/account/undo-email-change";

type Outcome = { state: "working" } | { state: "done"; email: string } | { state: "expired" } | { state: "failed" };

/** The token in `#token=…` (removed from the address bar once read). */
function takeToken(): string | undefined {
  const token = new URLSearchParams(location.hash.slice(1)).get("token") ?? undefined;
  if (location.hash !== "") history.replaceState(history.state, "", location.pathname);
  return token === "" ? undefined : token;
}

export function UndoEmailChangePage({ onSignIn }: { onSignIn: () => void }) {
  const t = useT();
  const [token] = useState(takeToken);
  const [outcome, setOutcome] = useState<Outcome>(token === undefined ? { state: "expired" } : { state: "working" });
  const call = (tok: string) =>
    undoEmailChange(tok).then(
      (email) => setOutcome({ state: "done", email }),
      (e: unknown) => {
        const kind = errorKind(e);
        setOutcome(kind === "network" || kind === "server" || kind === "rateLimited" ? { state: "failed" } : { state: "expired" });
      },
    );
  const retry = () => {
    if (token === undefined) return;
    setOutcome({ state: "working" });
    void call(token);
  };
  // Once per page: the undo is single-use.
  useEffect(() => {
    if (token !== undefined) void call(token);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Stack grow align="center" justify="center" padding="6" background="bg.window" testID="undo-email-change">
      <Surface material="elevated" width="dialog" radius="xl" padding="8">
        <Stack gap="6">
          <Stack direction="horizontal" align="center">
            <Logo variant="lockup" size="avatar.md" />
          </Stack>
          {outcome.state === "working" ? (
            <Stack direction="horizontal" gap="3" align="center" testID="undo-email-working">
              <Spinner />
              <Text color="text.secondary">{t("account.undoEmail.working")}</Text>
            </Stack>
          ) : outcome.state === "done" ? (
            <Stack gap="4" testID="undo-email-done">
              <Stack direction="horizontal" gap="2" align="center">
                <Icon name="checkCircle" size="icon.lg" color="status.success" />
                <Text variant="title2" weight="bold">
                  {t("account.undoEmail.doneTitle")}
                </Text>
              </Stack>
              <Text color="text.secondary">{t("account.undoEmail.doneBody", { email: outcome.email })}</Text>
              <Button label={t("account.undoEmail.signIn")} variant="primary" size="lg" onPress={onSignIn} testID="undo-email-sign-in" />
            </Stack>
          ) : outcome.state === "expired" ? (
            <Stack gap="4" testID="undo-email-expired">
              <Text variant="title2" weight="bold">
                {t("account.undoEmail.expiredTitle")}
              </Text>
              <Text color="text.secondary">{t("account.undoEmail.expiredBody")}</Text>
              <Button label={t("account.undoEmail.signIn")} variant="secondary" size="lg" onPress={onSignIn} testID="undo-email-sign-in" />
            </Stack>
          ) : (
            <Stack gap="4" testID="undo-email-failed">
              <Text variant="title2" weight="bold">
                {t("account.undoEmail.failedTitle")}
              </Text>
              <Text color="text.secondary">{t("auth.error.network")}</Text>
              <Button label={t("common.retry")} icon="reload" variant="primary" size="lg" onPress={retry} testID="undo-email-retry" />
            </Stack>
          )}
        </Stack>
      </Surface>
    </Stack>
  );
}
